import { useEffect, useState } from 'react';
import { post } from '../api';
import { completeRegistration, loadPasskeys, logRegistrationFailure, reconcileRegistrationFailure, registrationMessage, type PasskeyInfo } from '../passkey-registration';
import { useI18n } from '../i18n';
import { quickSignIn, registerQuickSignIn, safeStudentReturn, supportsQuickSignIn, supportsQuickSignInSetup } from '../passkeys';
import { Notice } from './Layout';

function useCapability(check = supportsQuickSignIn) {
  const [supported, setSupported] = useState(false);
  useEffect(() => { let active = true; void check().then((value) => { if (active) setSupported(value); });
    return () => { active = false; }; }, [check]);
  return supported;
}

export function QuickSignIn({ challenge, method, onEmail }: {
  challenge: string; method: 'qr' | 'code'; onEmail: () => void;
}) {
  const { language } = useI18n(); const zh = language === 'zh';
  const supported = useCapability();
  const [busy, setBusy] = useState(false); const [failed, setFailed] = useState(false);
  if (!supported) return null;
  async function signIn() {
    setBusy(true); setFailed(false);
    try { const result = await quickSignIn(challenge, method); window.location.replace(safeStudentReturn(result.returnTo)); }
    catch { setFailed(true); onEmail(); }
    finally { setBusy(false); }
  }
  return <div className="recovery-panel">
    <button className="secondary-button wide" disabled={busy} onClick={() => void signIn()}>
      {busy ? (zh ? '登入中…' : 'Signing in…') : (zh ? '快速登入' : 'Quick sign-in')}
    </button>
    <p className="muted">{zh ? '已設定快速登入？使用 Face ID、Touch ID 或裝置解鎖。亦可繼續使用電郵。' : 'Already set up? Use Face ID, Touch ID or device unlock. Email sign-in is always available.'}</p>
    {failed && <Notice>{zh ? '未完成快速登入。你可以重試或使用下方電郵登入。' : 'Quick sign-in was not completed. Try again or use email below.'}</Notice>}
  </div>;
}

export function QuickSignInSettings() {
  const { language } = useI18n(); const zh = language === 'zh'; const supported = useCapability(supportsQuickSignInSetup);
  const [keys, setKeys] = useState<PasskeyInfo[]>([]); const [busy, setBusy] = useState(false); const [message, setMessage] = useState('');
  async function refresh() { setKeys(await loadPasskeys()); }
  useEffect(() => { void refresh().catch((error) => logRegistrationFailure('list_refresh', error)); }, []);
  async function setup() {
    setBusy(true); setMessage('');
    try {
      await completeRegistration(registerQuickSignIn, refresh,
        () => setMessage(registrationMessage('ready', zh)));
    }
    catch (error) {
      const outcome = await reconcileRegistrationFailure(error);
      if (outcome.keys) setKeys(outcome.keys);
      setMessage(registrationMessage(outcome.result, zh));
    }
    finally { setBusy(false); }
  }
  async function revoke(credentialId: string) {
    setBusy(true);
    try { await post('/api/student/passkeys/revoke', { credentialId }); await refresh(); setMessage(zh ? '已移除快速登入。' : 'Quick sign-in removed.'); }
    catch { setMessage(zh ? '未能移除，請重試。' : 'Could not remove. Please retry.'); }
    finally { setBusy(false); }
  }
  return <section className="member-card"><h2>{zh ? '快速登入（自選）' : 'Quick sign-in (optional)'}</h2>
    <p>{zh ? '使用 Face ID、Touch ID 或裝置解鎖。我們不會取得你的生物辨識資料或裝置 PIN。電郵登入會繼續保留。' : 'Use Face ID, Touch ID or device unlock. We never receive your biometric data or device PIN. Email sign-in remains available.'}</p>
    {supported ? <button className="secondary-button" disabled={busy} onClick={() => void setup()}>{zh ? '設定快速登入' : 'Set up quick sign-in'}</button>
      : <p className="muted">{zh ? '此瀏覽器未提供裝置快速登入。你仍可使用電郵登入；或在 Safari / Chrome 開啟。' : 'Device quick sign-in is unavailable in this browser. Use email, or open in Safari / Chrome.'}</p>}
    {keys.map((key, index) => <div className="record-line" key={key.credential_id}><span>{zh ? '快速登入' : 'Passkey'} {index + 1} — {key.created_at.slice(0, 10)}</span>
      <button className="text-button" disabled={busy} onClick={() => void revoke(key.credential_id)}>{zh ? '移除' : 'Remove'}</button></div>)}
    {message && <Notice>{message}</Notice>}
  </section>;
}
